import { create } from "zustand";
import { persist, devtools } from "zustand/middleware";
import { createAuthGuardedStorage, createAuthSlice, type AuthSlice } from "@njbsoft/store";
import { createThemeSlice, type ThemeSlice } from "@njbsoft/store";
import { createLocaleSlice, type LocaleSlice } from "@njbsoft/store";
import { createOrgSlice, type OrgSlice } from "@njbsoft/store";
import { createViewSlice, type ViewSlice } from "@njbsoft/store";
import { createRecentPagesSlice, type RecentPagesSlice } from "@njbsoft/store";
import { isNavView, DEFAULT_VIEW } from "@/lib/nav-domains";
import { attachRecentPagesSessionSync } from "./recent-pages-session-sync";

// Combine all slices into a single bounded store
type AppStore = AuthSlice & ThemeSlice & LocaleSlice & OrgSlice & ViewSlice & RecentPagesSlice;

/** The localStorage key the persist middleware writes — the channel sibling
 *  tabs observe each other's token rotations and sign-outs through. */
export const WEB_STORE_PERSIST_KEY = "njbsoft-web-store";

/** What survives a reload. Lifted out of the persist options so the
 *  guarded storage can be typed on the PERSISTED shape rather than the whole
 *  store — the two must agree, and deriving one from the other is what keeps
 *  them agreeing. */
const partializeSession = (state: AppStore) => ({
  // Access token persisted alongside refresh token -- see security.md for threat model.
  token: state.token,
  refreshToken: state.refreshToken,
  expiresAt: state.expiresAt,
  // Ordered against what is already persisted before every write.
  rotatedAt: state.rotatedAt,
  user: state.user,
  themePreference: state.themePreference,
  accentColor: state.accentColor,
  localePreference: state.localePreference,
  activeOrgId: state.activeOrgId,
  activeOrgRole: state.activeOrgRole,
  activeOrgName: state.activeOrgName,
  activeOrgSlug: state.activeOrgSlug,
  activeOrgRequireMfa: state.activeOrgRequireMfa,
  orgResolved: state.orgResolved,
  // Sidebar "view by" nav filter survives page reloads. Stale
  // persisted values (e.g. a removed nav domain, or "all" from a
  // debug session) are normalized to DEFAULT_VIEW in merge below.
  activeView: state.activeView,
});
type PersistedAppStore = ReturnType<typeof partializeSession>;

/** The session's persist channel, guarded so no write can move the token pair
 *  BACKWARDS (see `@njbsoft/store`'s auth-persist). */
const authStorage = createAuthGuardedStorage<PersistedAppStore>();

export const useAppStore = create<AppStore>()(
  // devtools enabled only in development to avoid leaking state in production
  devtools(
    persist(
      (...a) => ({
        ...createAuthSlice(...a),
        ...createThemeSlice(...a),
        ...createLocaleSlice(...a),
        ...createOrgSlice(...a),
        ...createViewSlice(...a),
        // Absent from partialize by design: the visited-page trail rides
        // per-tab sessionStorage instead (see recent-pages-session-sync).
        ...createRecentPagesSlice(...a),
        // The shared view slice defaults to the generic "all" wildcard; the web
        // app focuses on Backflow, so override the initial view to DEFAULT_VIEW.
        activeView: DEFAULT_VIEW,
      }),
      {
        name: WEB_STORE_PERSIST_KEY,
        storage: authStorage,
        partialize: partializeSession,
        merge: (persisted, current) => {
          const p = (persisted ?? {}) as Partial<AppStore>;
          return {
            ...current,
            ...p,
            isAuthenticated: !!p.refreshToken,
            // A persisted view that is no longer a known nav view — including
            // "all" in a deployed build, where it is not an exposed option —
            // silently falls back to DEFAULT_VIEW (Backflow).
            activeView: isNavView(p.activeView) ? p.activeView : DEFAULT_VIEW,
          };
        },
      },
    ),
    { name: "SAMS v2 Web", enabled: import.meta.env.DEV },
  ),
);

// The adapter refuses a write that would move the token pair backwards and
// hands back the pair that survived; this catches THIS tab's memory up to it,
// so the context that lost the race is not left holding a spent pair to replay.
authStorage.onKeptNewer((tokens) => {
  useAppStore.getState().adoptTokens(tokens);
});

attachRecentPagesSessionSync(useAppStore);
